import { HttpClient } from '@angular/common/http';
import {
  Translation,
  TranslocoLoader,
  TranslocoModule,
  TranslocoService,
  provideTransloco,
} from '@ngneat/transloco';
import { APP_INITIALIZER, inject, Injectable, isDevMode, NgModule } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { LocalStorageConfigKeys } from '@config/app-storage/local-storage.config';
import {
  TranslocoAvailableLanguages,
  TranslocoConfigVariable,
} from './transloco.config';
import { environment } from '@config/environments/environment';

@Injectable({ providedIn: 'root' })
export class TranslocoHttpLoader implements TranslocoLoader {
  private http = inject(HttpClient);

  getTranslation(lang: string) {
    // Chỉ thêm baseHref trong production
    const prefix = environment.application.baseHref;
    return this.http.get<Translation>(
      prefix +
        TranslocoConfigVariable.i18nAssetsPath +
        // Mốc thời gian phá cache MỖI LẦN GỌI: bản dịch sửa xong là mọi máy
        // thấy ngay, không phải chờ hết hạn cache hay bảo sinh viên xoá cache.
        // Đổi lại mỗi lượt tải trang mất một vòng mạng cho file này — nên nó
        // được nạp SỚM và có người chờ, xem `APP_INITIALIZER` bên dưới.
        `${lang}.json?v=${new Date().getTime()}`
    );
  }
}

@NgModule({
  imports: [TranslocoModule],
  exports: [TranslocoModule],
  providers: [
    provideTransloco({
      config: {
        availableLangs: TranslocoAvailableLanguages.map(
          (language) => language.id
        ),
        defaultLang:
          localStorage
            .getItem(LocalStorageConfigKeys.global.transloco.currentLanguage)
            ?.replace(/"/g, '') || TranslocoConfigVariable.defaultLanguage,
        fallbackLang: TranslocoConfigVariable.defaultLanguage,

        reRenderOnLangChange: true,

        prodMode: !isDevMode(), // TODO: <---- Need to change in production mode (.env)
      },
      loader: TranslocoHttpLoader,
    }),

    /**
     * Nạp bảng dịch XONG rồi mới cho ứng dụng vẽ khung đầu tiên.
     *
     * ── Vì sao cần ────────────────────────────────────────────────────────────
     * `transloco.translate()` là hàm ĐỒNG BỘ: gọi lúc bảng dịch chưa về thì nó
     * trả lại chính cái khoá. Ống `| transloco` trong template tự vẽ lại khi
     * file về, nhưng mã TypeScript gọi thẳng `translate()` thì không có ai nhắc.
     *
     * Máy vào trang LẦN ĐẦU chưa có gì trong cache: file dịch phải xếp hàng sau
     * toàn bộ js/css/ảnh nền, về muộn hẳn — đủ lâu để sinh viên kịp đọc thấy
     * `workspace.posted.days` nằm giữa màn hình. Vào lại lần sau thì js/css đã
     * nằm trong cache, file dịch về gần như tức thì nên chữ hiện đúng ngay.
     * Đó là lý do lỗi này luôn hiện ra dạng "chỉ sai lần đầu, F5 là hết" và rất
     * dễ lọt khi kiểm thử.
     *
     * Chặn MỘT lần ở đây thì cả ứng dụng khỏi phải tự phòng từng chỗ.
     *
     * ── Giá phải trả ──────────────────────────────────────────────────────────
     * Khung đầu tiên vẽ muộn hơn đúng một vòng mạng. File dịch nhỏ (~19KB,
     * bản tiếng Anh lớn nhất ~94KB) và ứng dụng vốn ĐÃ tải nó ở mọi lượt vào
     * trang rồi — thay đổi ở đây chỉ là chờ nó xong trước khi vẽ, chứ không
     * thêm việc gì mới.
     *
     * Nạp hỏng (mất mạng, sai đường dẫn) thì VẪN cho ứng dụng chạy tiếp: màn
     * hình hiện khoá thô còn hơn màn hình trắng không vào được.
     */
    {
      provide: APP_INITIALIZER,
      multi: true,
      deps: [TranslocoService],
      useFactory: (transloco: TranslocoService) => () =>
        firstValueFrom(transloco.load(transloco.getActiveLang())).catch(() => undefined),
    },
  ],
})
export class TranslocoRootModule {}
